import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, qs } from '../lib/api'
import { useAuth } from '../lib/auth'
import { dateOnly } from '../lib/format'
import type { Driver, Fleet, ListResponse } from '../lib/types'
import { Page } from '../components/Shell'
import { Icon } from '../components/Icon'
import { DataTable, Toolbar, useSearch, type Column } from '../components/DataTable'
import { Button, Chip, Field, Modal, Panel, Toggle, toast } from '../components/ui'

export function Drivers() {
  const { can } = useAuth()
  const qc = useQueryClient()
  const search = useSearch()
  const [offset, setOffset] = useState(0)
  const [editing, setEditing] = useState<Driver | null>(null)
  const [creating, setCreating] = useState(false)
  const limit = 50

  const list = useQuery({
    queryKey: ['drivers', search.debounced, offset],
    queryFn: () =>
      api<ListResponse<Driver>>(`/drivers${qs({ q: search.debounced, limit, offset })}`),
  })

  const remove = useMutation({
    mutationFn: (id: string) => api(`/drivers/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      toast('Driver removed', 'ok')
      void qc.invalidateQueries({ queryKey: ['drivers'] })
    },
    onError: (e: Error) => toast(e.message, 'error'),
  })

  const columns: Column<Driver>[] = [
    {
      key: 'name',
      header: 'Driver',
      render: (d) => (
        <div className="flex items-center gap-2.5 min-w-0">
          <span
            className="w-6 h-6 grid place-items-center rounded-[3px] text-[10px] font-semibold shrink-0"
            style={{ background: 'var(--color-raised)', color: 'var(--color-ink-2)' }}
          >
            {d.fullName
              .split(/\s+/)
              .slice(0, 2)
              .map((p) => p[0]?.toUpperCase())
              .join('')}
          </span>
          <div className="min-w-0">
            <div className="text-[var(--color-ink)] truncate">{d.fullName}</div>
            {d.employeeNo && (
              <div className="text-[11px] text-[var(--color-ink-4)]">#{d.employeeNo}</div>
            )}
          </div>
        </div>
      ),
    },
    {
      key: 'vehicle',
      header: 'Driving',
      render: (d) =>
        d.vehicleName ? (
          <Chip tone="accent">{d.vehicleName}</Chip>
        ) : (
          <span className="text-[var(--color-ink-4)]">Not assigned</span>
        ),
    },
    {
      key: 'contact',
      header: 'Contact',
      render: (d) => (
        <div className="text-[12px]">
          {d.phone && <div className="tnum">{d.phone}</div>}
          {d.email && <div className="text-[var(--color-ink-4)] truncate">{d.email}</div>}
          {!d.phone && !d.email && '—'}
        </div>
      ),
    },
    {
      key: 'licence',
      header: 'Licence',
      render: (d) => (
        <div className="text-[12px]">
          {d.licenceNo ? <span className="tnum">{d.licenceNo}</span> : '—'}
          {d.licenceExpiry && (
            <div
              className="text-[11px]"
              style={{
                color: new Date(d.licenceExpiry) < new Date() ? 'var(--text-critical)' : 'var(--color-ink-4)',
              }}
            >
              expires {dateOnly(d.licenceExpiry)}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'key',
      header: 'Driver key',
      render: (d) =>
        d.ibuttonId ? (
          <span className="tnum text-[12px]" title="Trips are attributed automatically when this key is presented">
            {d.ibuttonId}
          </span>
        ) : (
          <span className="text-[var(--color-ink-4)]">—</span>
        ),
    },
    {
      key: 'active',
      header: 'State',
      width: '80px',
      render: (d) => (d.active ? <Chip tone="ok">Active</Chip> : <Chip tone="muted">Inactive</Chip>),
    },
    {
      key: 'actions',
      header: '',
      width: '70px',
      render: (d) =>
        can('driver.manage') && (
          <div className="flex justify-end gap-1">
            <button className="btn btn-ghost btn-sm px-1.5" onClick={() => setEditing(d)} title="Edit">
              <Icon name="edit" size={13} />
            </button>
            <button
              className="btn btn-ghost btn-sm px-1.5"
              style={{ color: 'var(--text-critical)' }}
              title="Remove"
              onClick={() => confirm(`Remove ${d.fullName}? Past trips keep their attribution.`) && remove.mutate(d.id)}
            >
              <Icon name="trash" size={13} />
            </button>
          </div>
        ),
    },
  ]

  return (
    <Page
      title="Drivers"
      subtitle={list.data ? `${list.data.total} on the books` : undefined}
      actions={
        can('driver.manage') && (
          <Button variant="primary" onClick={() => setCreating(true)}>
            <Icon name="plus" size={13} />
            Add driver
          </Button>
        )
      }
    >
      <Toolbar search={search.value} onSearch={search.setValue} placeholder="Name or employee number…" />

      <Panel padded={false}>
        <DataTable
          columns={columns}
          rows={list.data?.items}
          rowKey={(d) => d.id}
          loading={list.isLoading}
          error={list.error}
          onRetry={() => void list.refetch()}
          total={list.data?.total}
          limit={limit}
          offset={offset}
          onPage={setOffset}
          empty={{
            title: 'No drivers yet',
            body: 'Add drivers to attribute trips and build scorecards. A driver key lets the device do it automatically.',
            action: can('driver.manage') ? (
              <Button variant="primary" onClick={() => setCreating(true)}>
                Add the first driver
              </Button>
            ) : undefined,
          }}
        />
      </Panel>

      {(creating || editing) && (
        <DriverForm
          driver={editing}
          onClose={() => {
            setCreating(false)
            setEditing(null)
          }}
          onSaved={() => {
            setCreating(false)
            setEditing(null)
            void qc.invalidateQueries({ queryKey: ['drivers'] })
          }}
        />
      )}
    </Page>
  )
}

function DriverForm({
  driver,
  onClose,
  onSaved,
}: {
  driver: Driver | null
  onClose: () => void
  onSaved: () => void
}) {
  const [form, setForm] = useState({
    fullName: driver?.fullName ?? '',
    employeeNo: driver?.employeeNo ?? '',
    licenceNo: driver?.licenceNo ?? '',
    phone: driver?.phone ?? '',
    email: driver?.email ?? '',
    ibuttonId: driver?.ibuttonId ?? '',
    fleetId: driver?.fleetId ?? '',
    active: driver?.active ?? true,
  })

  const fleets = useQuery({ queryKey: ['fleets'], queryFn: () => api<{ items: Fleet[] }>('/fleets') })

  const save = useMutation({
    mutationFn: () => {
      const body = {
        fullName: form.fullName.trim(),
        employeeNo: form.employeeNo.trim(),
        licenceNo: form.licenceNo.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        ibuttonId: form.ibuttonId.trim(),
        fleetId: form.fleetId || null,
        active: form.active,
      }
      return driver ? api(`/drivers/${driver.id}`, { method: 'PATCH', body }) : api('/drivers', { body })
    },
    onSuccess: () => {
      toast(driver ? 'Driver updated' : 'Driver added', 'ok')
      onSaved()
    },
    onError: (e: Error) => toast(e.message, 'error'),
  })

  const set = (k: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [k]: e.target.value }))

  return (
    <Modal
      open
      onClose={onClose}
      title={driver ? `Edit ${driver.fullName}` : 'Add driver'}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!form.fullName.trim() || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
        </>
      }
    >
      <div className="grid sm:grid-cols-2 gap-3.5">
        <div className="sm:col-span-2">
          <Field label="Full name" required>
            <input className="field" value={form.fullName} onChange={set('fullName')} autoFocus />
          </Field>
        </div>
        <Field label="Employee number">
          <input className="field tnum" value={form.employeeNo} onChange={set('employeeNo')} />
        </Field>
        <Field label="Fleet">
          <select className="field" value={form.fleetId} onChange={set('fleetId')}>
            <option value="">Unassigned</option>
            {fleets.data?.items.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Phone">
          <input className="field tnum" value={form.phone} onChange={set('phone')} placeholder="+31 6 1234 5678" />
        </Field>
        <Field label="Email">
          <input className="field" type="email" value={form.email} onChange={set('email')} />
        </Field>
        <Field label="Licence number">
          <input className="field tnum" value={form.licenceNo} onChange={set('licenceNo')} />
        </Field>
        <Field
          label="Driver key"
          hint="iButton or RFID value the device reports"
        >
          <input className="field tnum" value={form.ibuttonId} onChange={set('ibuttonId')} placeholder="A1B2C3D4E5F60718" />
        </Field>
        <div className="sm:col-span-2 pt-1">
          <Toggle
            checked={form.active}
            onChange={(v) => setForm((f) => ({ ...f, active: v }))}
            label="Driver is active"
          />
        </div>
      </div>
    </Modal>
  )
}
